import { useContext, useRef } from "react";
import editorStyle from "../../reportWriter.module.css";

import { Bold, Italic, Strikethrough, Code, Image, Ban, CheckCheck } from "lucide-react";

import MarkdownDisplay from "../../../../../Common/MarkdownDisplay/MarkdownDisplay.tsx";
import SlateEditor, { ListingRef } from "./components/SlateEditor/SlateEditor.tsx";
import Tooltip from "../../../../../Common/Tooltips/Tooltip/Tooltip.tsx";
import Button from "../../../../../Common/Button/Button.tsx";
import { AppContext } from "../../../../../../contexts/AppContext.ts";
import { EditorContext } from "../../../../../../contexts/EditorContext.ts";
import CVSSCalculator from "../CvssCalculator/CvssCalculator.tsx";

interface Props {
  scrollRef: React.Ref<HTMLDivElement>;

  confirmCancelChanges: () => void;
  saveSection: (finalValue: string) => void;

  isCvss: boolean;
}

function EditorWidget({ scrollRef, confirmCancelChanges, saveSection, isCvss }: Readonly<Props>) {
  const { setShowModal } = useContext(AppContext);
  const { value } = useContext(EditorContext);

  const editorRef = useRef<ListingRef>(null);
  function setMark(mark: string, mark2?: string) {
    editorRef.current?.selectionMark(mark, mark2);
  }

  function flushSave() {
    saveSection(editorRef.current?.localValue ?? value);
  }

  function advertiseImageInsertion() {
    setShowModal("generic", {
      content: (
        <p className={editorStyle.imageAdvertisement}>
          To add a Screenshot, simply <span className={editorStyle.blueText}>paste an image</span> from your
          <span className={editorStyle.blueText}> clipboard</span> using Ctrl + V
        </p>
      ),
    });
  }

  function getTooltips() {
    return (
      <>
        <Tooltip
          trigger={
            <button
              className={editorStyle.editorButton}
              onClick={() => {
                setMark("**");
              }}
            >
              <Bold className={editorStyle.editorImageButton} stroke="white" strokeWidth="1.5" />
            </button>
          }
          description="[B] Bold"
          showArrow
          highContrast
        />

        <Tooltip
          trigger={
            <button
              className={editorStyle.editorButton}
              onClick={() => {
                setMark("_");
              }}
            >
              <Italic className={editorStyle.editorImageButton} stroke="white" strokeWidth="1.5" />
            </button>
          }
          description="[I] Italic"
          showArrow
          highContrast
        />
        <Tooltip
          trigger={
            <button
              className={editorStyle.editorButton}
              onClick={() => {
                setMark("~~");
              }}
            >
              <Strikethrough className={editorStyle.editorImageButton} stroke="white" strokeWidth="1.5" />
            </button>
          }
          description="[X] Strikethrough"
          showArrow
          highContrast
        />

        <Tooltip
          trigger={
            <button
              className={editorStyle.editorButton}
              onClick={() => {
                setMark("\n```\n");
              }}
            >
              <Code className={editorStyle.editorImageButton} stroke="white" strokeWidth="1.5" />
            </button>
          }
          description="[j] Code Block"
          showArrow
          highContrast
        />

        <Tooltip
          trigger={
            <button
              className={editorStyle.editorButton}
              onClick={() => {
                setMark("![Alt text](", ")");
              }}
            >
              <Image className={editorStyle.editorImageButton} stroke="white" strokeWidth="1.5" />
            </button>
          }
          description="[p] Upload Photo"
          showArrow
          highContrast
        />
      </>
    );
  }

  return (
    <div className={editorStyle.editorWrapper}>
      <div className={editorStyle.allOptions}>
        <div className={editorStyle.editorOptions}>{getTooltips()}</div>
        <Button variant="primary" color="blue" textcolor="light" onClick={advertiseImageInsertion}>
          <Image className={editorStyle.iconMargin + " " + editorStyle.smallIcon} stroke="white" strokeWidth="1.5" />
          <span className={editorStyle.textSize}>Add Snapshot</span>
        </Button>
      </div>
      <div className={editorStyle.editorView}>
        {isCvss ? (
          <div ref={scrollRef} className={editorStyle.soloEditor}>
            <CVSSCalculator editorRef={editorRef}/>
          </div>
        ) : (
          <>
            <div ref={scrollRef} className={editorStyle.editorBox}>
              <SlateEditor ref={editorRef} />
            </div>
            <div className={editorStyle.previewBox}>
              <MarkdownDisplay value={value} />
            </div>
          </>
        )}
      </div>

      <div className={editorStyle.bottomOptions}>
        <Button variant="primary" color="red" textcolor="light" onClick={confirmCancelChanges}>
          <Ban className={editorStyle.iconMargin + " " + editorStyle.smallIcon} stroke="white" strokeWidth="1.5" />
          Cancel
        </Button>
        <Button variant="primary" color="green" textcolor="light" onClick={flushSave}>
          <CheckCheck
            className={editorStyle.iconMargin + " " + editorStyle.smallIcon}
            stroke="white"
            strokeWidth="1.5"
          />
          Save
        </Button>
      </div>
    </div>
  );
}

export default EditorWidget;
